import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Stack,
  Divider,
  Paper,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon,
  Bookmark as BookmarkIcon,
  TrendingUp as TrendingIcon,
  FiberNew as NewIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Upload from "../Upload";
import { useDispatch } from "react-redux";
import { createBlog, updateBlog } from "../../redux/slices/blog";
import { calculateReadingTime } from "../../lib/utils";
import BlogTextEditor from "./BogTextEditor";

const contentBlockTypes = ["heading", "paragraph", "blockquote", "image"];
const initialFormState = {
  title: "",
  slug: "",
  excerpt: "",
  tags: [],
  coverImage: "",
  content: [],
  categories: [],
  isFeatured: false,
  isTrending: false,
  isLatest: true,
};

export default function BlogForm({ open, onClose, selected }) {
  const [formData, setFormData] = useState(initialFormState);
  const [tagInput, setTagInput] = useState("");
  const [categoryInput, setCategoryInput] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [draggedItem, setDraggedItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blockUploading, setBlockUploading] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    if (open && selected) {
      setFormData(selected);
      setActiveStep(0);
    } else {
      if (formData.coverImage.startsWith("blob:")) {
        URL.revokeObjectURL(formData.coverImage);
      }
      setFormData(initialFormState);
      setActiveStep(0);
      setTagInput("");
      setCategoryInput("");
      setDraggedItem(null);
      setBlockUploading({});
    }
  }, [open, selected]);

  const duration = useMemo(() => {
    return calculateReadingTime(formData.content);
  }, [formData.content]);

  const steps = ["Basic Info", "Content", "Settings"];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Auto-generate slug from title
    if (field === "title" && !selected) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      handleChange("tags", [...formData.tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    handleChange(
      "tags",
      formData.tags.filter((t) => t !== tag)
    );
  };

  const handleAddCategory = () => {
    if (
      categoryInput.trim() &&
      !formData.categories.includes(categoryInput.trim())
    ) {
      handleChange("categories", [
        ...formData.categories,
        categoryInput.trim(),
      ]);
      setCategoryInput("");
    }
  };

  const handleRemoveCategory = (cat) => {
    handleChange(
      "categories",
      formData.categories.filter((c) => c !== cat)
    );
  };

  const handleAddContentBlock = () => {
    handleChange("content", [
      ...formData.content,
      { type: "paragraph", text: "" },
    ]);
  };

  const handleUpdateContentBlock = (index, field, value) => {
    const newContent = [...formData.content];
    newContent[index] = { ...newContent[index], [field]: value };
    handleChange("content", newContent);
  };

  const handleRemoveContentBlock = (index) => {
    const block = formData.content[index];
    // Revoke blob URL if it's an image block
    if (block.type === "image" && block.url && block.url.startsWith("blob:")) {
      URL.revokeObjectURL(block.url);
    }
    handleChange(
      "content",
      formData.content.filter((_, i) => i !== index)
    );
  };

  const handleBlockImageUpload = (index, { uploading, url, error }) => {
    setBlockUploading((prev) => ({ ...prev, [index]: uploading }));
    if (url) {
      handleUpdateContentBlock(index, "url", url);
    }
    if (error) {
      toast.error(error);
    }
  };

  const handleDragStart = (index) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newContent = [...formData.content];
    const draggedBlock = newContent[draggedItem];
    newContent.splice(draggedItem, 1);
    newContent.splice(index, 0, draggedBlock);

    setFormData((prev) => ({ ...prev, content: newContent }));
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, 2));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.slug) {
      toast.error("Please fill in required fields: title and slug");
      return;
    }

    if (formData.content.length === 0) {
      toast.error("Please add at least one content block");
      return;
    }

    // Validate image blocks
    const invalidImageBlocks = formData.content.filter(
      (block) => block.type === "image" && !block.url
    );
    if (invalidImageBlocks.length > 0) {
      toast.error("Please upload images for all image blocks");
      return;
    }

    setSubmitting(true);
    let success;
    if (selected) {
      success = await dispatch(
        updateBlog({ ...formData, duration }, selected._id)
      );
    } else {
      success = await dispatch(createBlog({ ...formData, duration }));
    }
    setSubmitting(false);
    if (success) {
      onClose();
    }
  };

  const handleUpload = ({ uploading, url, error }) => {
    setUploading(uploading);
    if (url) setFormData((prev) => ({ ...prev, coverImage: url }));
    if (error) toast.error(error);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={3}>
            <TextField
              label="Post Title"
              fullWidth
              required
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="Enter an engaging title..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EditIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="URL Slug"
              fullWidth
              required
              value={formData.slug}
              onChange={(e) =>
                handleChange(
                  "slug",
                  e.target.value.toLowerCase().replace(/\s+/g, "-")
                )
              }
              helperText="URL-friendly identifier (auto-generated from title)"
              placeholder="post-url-slug"
            />

            <TextField
              label="Excerpt"
              fullWidth
              multiline
              rows={3}
              value={formData.excerpt}
              onChange={(e) => handleChange("excerpt", e.target.value)}
              placeholder="Brief summary of your post..."
              helperText={`${formData.excerpt.length}/200 characters`}
            />

            <Upload
              stream={handleUpload}
              url={formData.coverImage}
              uploading={uploading}
              onRemove={() => {
                URL.revokeObjectURL(formData.coverImage);
                setFormData((prev) => ({ ...prev, coverImage: "" }));
              }}
            />

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Tags
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add a tag (e.g., react, tutorial)"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), handleAddTag())
                  }
                />
                <Button
                  onClick={handleAddTag}
                  variant="contained"
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Categories
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add a category"
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" &&
                    (e.preventDefault(), handleAddCategory())
                  }
                />
                <Button
                  onClick={handleAddCategory}
                  variant="contained"
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {formData.categories.map((cat) => (
                  <Chip
                    key={cat}
                    label={cat}
                    onDelete={() => handleRemoveCategory(cat)}
                    size="small"
                    color="secondary"
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" fontWeight="600">
                Content Blocks
                <span className="ml-2 px-2 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded-md">
                  {duration} read
                </span>
              </Typography>
              <Button
                onClick={handleAddContentBlock}
                startIcon={<AddIcon />}
                variant="contained"
              >
                Add Block
              </Button>
            </Box>

            {formData.content.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: "center", bgcolor: "#f5f5f5" }}>
                <Typography color="text.secondary">
                  No content blocks yet. Click "Add Block" to start writing.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2}>
                {formData.content.map((block, index) => (
                  <Paper
                    key={index}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      p: 2,
                      cursor: "move",
                      border: "2px solid",
                      borderColor:
                        draggedItem === index ? "primary.main" : "transparent",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "primary.light",
                        boxShadow: 2,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 2, alignItems: "start" }}>
                      <DragIcon
                        sx={{ color: "text.secondary", mt: 1, cursor: "grab" }}
                      />

                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Block Type</InputLabel>
                            <Select
                              value={block.type}
                              label="Block Type"
                              onChange={(e) => {
                                const newType = e.target.value;
                                const updatedBlock = { type: newType };

                                // Initialize fields based on type
                                if (newType === "image") {
                                  updatedBlock.url = "";
                                  updatedBlock.alt = "";
                                } else {
                                  updatedBlock.text = "";
                                }

                                const newContent = [...formData.content];
                                newContent[index] = updatedBlock;
                                handleChange("content", newContent);
                              }}
                            >
                              {contentBlockTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <Chip
                            label={`Block ${index + 1}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>

                        {block.type === "image" ? (
                          <Stack spacing={2}>
                            <Upload
                              stream={(data) =>
                                handleBlockImageUpload(index, data)
                              }
                              url={block.url || ""}
                              uploading={blockUploading[index] || false}
                              onRemove={() => {
                                if (
                                  block.url &&
                                  block.url.startsWith("blob:")
                                ) {
                                  URL.revokeObjectURL(block.url);
                                }
                                handleUpdateContentBlock(index, "url", "");
                              }}
                            />
                            <TextField
                              fullWidth
                              placeholder="Enter alt text for accessibility (e.g., 'A person coding on laptop')"
                              value={block.alt || ""}
                              onChange={(e) =>
                                handleUpdateContentBlock(
                                  index,
                                  "alt",
                                  e.target.value
                                )
                              }
                              label="Alt Text"
                              helperText="Describe the image for accessibility"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <ImageIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Stack>
                        ) : block.type !== "paragraph" ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={block.type === "heading" ? 2 : 4}
                            placeholder={`Enter ${block.type} content...`}
                            value={block.text || ""}
                            onChange={(e) =>
                              handleUpdateContentBlock(
                                index,
                                "text",
                                e.target.value
                              )
                            }
                            variant="outlined"
                          />
                        ) : (
                          <BlogTextEditor
                            value={block.text}
                            onChange={(value) =>
                              handleUpdateContentBlock(index, "text", value)
                            }
                            placeholder={`Enter ${block.type} content...`}
                          />
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() => handleRemoveContentBlock(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="600">
              Post Settings
            </Typography>

            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2 }}>
                Display Options
              </Typography>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BookmarkIcon color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        Featured Post
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Show in featured section
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={formData.isFeatured}
                    onChange={(e) =>
                      handleChange("isFeatured", e.target.checked)
                    }
                  />
                </Box>

                <Divider />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TrendingIcon color="secondary" />
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        Trending Post
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Mark as trending content
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={formData.isTrending}
                    onChange={(e) =>
                      handleChange("isTrending", e.target.checked)
                    }
                  />
                </Box>

                <Divider />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <NewIcon color="success" />
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        Latest Post
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Show as new content
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={formData.isLatest}
                    onChange={(e) => handleChange("isLatest", e.target.checked)}
                  />
                </Box>
              </Stack>
            </Paper>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!submitting && onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <Box sx={{ p: 3, bgcolor: "primary.main", color: "white" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {selected ? "Edit Post" : "Create New Post"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {selected
                ? "Update your blog post"
                : "Share your thoughts with the world"}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={submitting}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ px: 3, pt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, pb: 3 }}>{renderStepContent()}</DialogContent>

      <Divider />

      <Box sx={{ p: 3, display: "flex", justifyContent: "space-between" }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || submitting}
          sx={{ textTransform: "none" }}
        >
          Back
        </Button>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="large"
              sx={{ textTransform: "none", px: 4 }}
              disabled={submitting}
              loading={submitting}
            >
              {selected ? "Update Post" : "Publish Post"}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              variant="contained"
              sx={{ textTransform: "none", px: 3 }}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
